import { Callout, toast } from "@/components/ui";
import {
  OBSERVATION_TRACKERS,
  type ObservationCode,
} from "@/constants/ObservationTrackers";
import type { ApiError } from "@/hooks/useApi";
import { captureError } from "@/lib/monitoring";
import { trackEvent } from "@/lib/tracking";
import { useCreateGenericObservation } from "@/network/useGenericObservations";
import {
  type CreateGenericObservationRequest,
  GroupItemAnswerType,
  QuickChartOptionClass,
} from "@/types/observations";
import { useParams } from "@tanstack/react-router";
import { ClipboardCheck, ClipboardMinus } from "lucide-react";
import React, { useState } from "react";
import { EditableRecord } from "./EditableRecord";
import {
  GenericObservationCodeDisplay,
  GenericObservationCodeForm,
} from "./GenericObservationCodeForm";
import {
  GenericObservationDateDisplay,
  GenericObservationDateForm,
} from "./GenericObservationDateForm";
import { GenericObservationHistoryModal } from "./GenericObservationHistoryModal";
import {
  GenericObservationMultiCodeDisplay,
  GenericObservationMultiCodeForm,
} from "./GenericObservationMultiCodeForm";
import {
  GenericObservationNumberDisplay,
  GenericObservationNumberForm,
} from "./GenericObservationNumberForm";
import {
  GenericObservationParagraphDisplay,
  GenericObservationParagraphForm,
} from "./GenericObservationParagraphForm";
import {
  GenericObservationPresenceDisplay,
  GenericObservationPresenceForm,
} from "./GenericObservationPresenceForm";
import {
  GenericObservationYesNoDisplay,
  GenericObservationYesNoForm,
} from "./GenericObservationYesNoForm";
import type { GenericObservationEditorProps } from "./types";

export const GenericObservationEditor = ({
  item,
  itemCategory,
  group,
}: GenericObservationEditorProps) => {
  const { patientId } = useParams({ strict: false });
  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const onError = (error: ApiError) => {
    toast.error(
      `Failed to save ${item.observationCodeFriendlyDisplay}: ${error.response?.data?.detail || error.message}`,
    );
  };
  const {
    mutate: createGenericObservation,
    isPending: isCreatingGenericObservation,
  } = useCreateGenericObservation({
    patientId: patientId as string,
    group,
    onSuccess: () => {
      // Track save action
      const tracker =
        OBSERVATION_TRACKERS[item.observationCode as ObservationCode];
      if (tracker?.SAVE) {
        trackEvent(tracker.SAVE);
      }

      setEditing(false);
    },
    onError,
  });
  const {
    mutate: quickChartGenericObservation,
    isPending: isQuickChartPending,
  } = useCreateGenericObservation({
    patientId: patientId as string,
    group,
    onError,
  });

  const [FormComponent, DisplayComponent] = React.useMemo(() => {
    switch (item.groupItemAnswerType) {
      case GroupItemAnswerType.CODE:
        return [GenericObservationCodeForm, GenericObservationCodeDisplay];
      case GroupItemAnswerType.MULTI_CODE:
        return [
          GenericObservationMultiCodeForm,
          GenericObservationMultiCodeDisplay,
        ];
      case GroupItemAnswerType.DATE:
        return [GenericObservationDateForm, GenericObservationDateDisplay];
      case GroupItemAnswerType.YES_NO:
        return [GenericObservationYesNoForm, GenericObservationYesNoDisplay];
      case GroupItemAnswerType.NUMBER:
        return [GenericObservationNumberForm, GenericObservationNumberDisplay];
      case GroupItemAnswerType.PARAGRAPH:
        return [
          GenericObservationParagraphForm,
          GenericObservationParagraphDisplay,
        ];
      case GroupItemAnswerType.PRESENCE:
        return [
          GenericObservationPresenceForm,
          GenericObservationPresenceDisplay,
        ];
      default:
        captureError(
          Error(
            `Unsupported group item answer type: ${item.groupItemAnswerType}`,
          ),
          {
            component: "GenericObservationEditor",
          },
        );
        return [null, null];
    }
  }, [item.groupItemAnswerType]);

  const onSubmit = (payload: CreateGenericObservationRequest) => {
    createGenericObservation(payload);
  };

  const onCancel = () => {
    // Track cancel action
    const tracker =
      OBSERVATION_TRACKERS[item.observationCode as ObservationCode];
    if (tracker?.CANCEL) {
      trackEvent(tracker.CANCEL);
    }

    setEditing(false);
  };

  const onEdit = () => {
    // Track edit action
    const tracker =
      OBSERVATION_TRACKERS[item.observationCode as ObservationCode];
    if (tracker?.EDIT) {
      trackEvent(tracker.EDIT);
    }

    setEditing(true);
  };

  const quickActions = item.quickChartOptions?.map((option) => ({
    label: option.value.friendlyDisplay,
    icon:
      option.optionClass === QuickChartOptionClass.DENIAL ? (
        <ClipboardMinus className="h-4 w-4 mr-2" />
      ) : (
        <ClipboardCheck className="h-4 w-4 mr-2" />
      ),
    onClick: () => {
      const tracker =
        OBSERVATION_TRACKERS[item.observationCode as ObservationCode];
      if (tracker && "QUICK_CHART" in tracker) {
        trackEvent(tracker.QUICK_CHART);
      }

      const valueCodeableConcept = {
        coding: [
          {
            code: option.value.value,
            system: option.value.system,
            display: option.value.display,
          },
        ],
        text: option.value.friendlyDisplay,
      };

      const isMultiCode =
        item.groupItemAnswerType === GroupItemAnswerType.MULTI_CODE;

      const payload: CreateGenericObservationRequest = {
        patientId: patientId as string,
        category: itemCategory,
        status: "final",
        code: {
          coding: [
            {
              code: item.observationCode,
              system: item.observationCodeSystem,
              display: item.observationCodeDisplay,
            },
          ],
          text: item.observationCodeFriendlyDisplay,
        },
        ...(isMultiCode
          ? { fhirValues: [{ valueCodeableConcept }] }
          : { fhirValue: { valueCodeableConcept } }),
      };

      quickChartGenericObservation(payload);
    },
  }));

  if (!DisplayComponent || !FormComponent) {
    return (
      <Callout
        className="w-full cursor-pointer"
        variant="danger"
        title="Unable to load this observation"
        description="If this error persists, please contact support."
      />
    );
  }
  return (
    <div
      key={item.observationCodeDisplay}
      className="flex flex-col flex-1 w-full"
    >
      <EditableRecord
        displayComponent={<DisplayComponent item={item} />}
        formComponent={
          <FormComponent
            patientId={patientId as string}
            item={item}
            itemCategory={itemCategory}
            onSubmit={onSubmit}
            onCancel={onCancel}
            isSubmitting={isCreatingGenericObservation}
          />
        }
        isEditing={editing}
        onEdit={onEdit}
        title={item.observationCodeFriendlyDisplay}
        onViewHistory={() => setShowHistory(true)}
        quickActions={quickActions}
        pending={isQuickChartPending}
      />
      <GenericObservationHistoryModal
        displayComponent={DisplayComponent}
        group={group}
        observationCode={item.observationCode}
        observationCodeFriendlyDisplay={item.observationCodeFriendlyDisplay}
        observationCodeSystem={item.observationCodeSystem}
        onOpenChange={setShowHistory}
        open={showHistory}
        patientId={patientId as string}
      />
    </div>
  );
};
